import { useState, useEffect, useMemo } from 'react';
import {
  Title, Group, Stack, Card, Table, Badge, TextInput, Button,
  Text, Code, ScrollArea, Box, ActionIcon, Tooltip, Select
} from '@mantine/core';
import {
  IconApi, IconSearch, IconRefresh, IconCopy, IconExternalLink, IconFilter
} from '@tabler/icons-react';
import api from '../api/axiosConfig';

export default function EndpointDashboard() {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Yeni: Seçili Router State'i
  const [selectedRouter, setSelectedRouter] = useState("ALL");

  useEffect(() => {
    endpointleriGetir();
  }, []);

  const endpointleriGetir = async () => {
    try {
      setLoading(true);
      const response = await api.get("/endpointler");
      setEndpoints(response.data.endpointler || []);
    } catch (error) {
      console.error("Endpointler alınamadı:", error);
    } finally {
      setLoading(false);
    }
  };

  // Yeni: Gelen verilerden benzersiz Router (Tag) isimlerini çıkarıyoruz
  const uniqueRouters = useMemo(() => {
    const routers = endpoints.map(ep => ep.router || "Genel");
    const unique = [...new Set(routers)]; // Tekrar edenleri temizle
    
    // Mantine Select için uygun formata çevir
    return [
      { value: "ALL", label: "Tüm Modüller" },
      ...unique.map(r => ({ value: r, label: r }))
    ];
  }, [endpoints]);

  // Güncellenmiş Filtreleme Mantığı (Hem Arama Hem Router)
  const filteredEndpoints = useMemo(() => {
    return endpoints.filter((endpoint) => {
      const path = endpoint.path || "";
      const fonksiyon = endpoint.fonksiyon || "";
      const router = endpoint.router || "Genel";

      // 1. Arama kelimesi kontrolü
      const matchesSearch = path.toLowerCase().includes(search.toLowerCase()) ||
                            fonksiyon.toLowerCase().includes(search.toLowerCase());
      
      // 2. Router (Modül) kontrolü
      const matchesRouter = selectedRouter === "ALL" || router === selectedRouter;

      return matchesSearch && matchesRouter;
    });
  }, [endpoints, search, selectedRouter]);

  const getMethodColor = (method) => {
    switch (method.toUpperCase()) {
      case 'GET': return 'teal';
      case 'POST': return 'blue';
      case 'PUT': return 'yellow';
      case 'PATCH': return 'grape';
      case 'DELETE': return 'red';
      default: return 'gray';
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group>
          <IconApi size={28} color="#228be6" />
          <Box>
            <Title order={2}>API Endpoint Yönetimi</Title>
            <Text size="sm" c="dimmed">
              Toplam Endpoint: {filteredEndpoints.length}
            </Text>
          </Box>
        </Group>

        <Button 
          leftSection={<IconRefresh size={16} />} 
          variant="light" 
          onClick={endpointleriGetir}
          loading={loading}
        >
          Yenile
        </Button>
      </Group>

      {/* Güncellenmiş Arama ve Filtreleme Alanı */}
      <Card withBorder shadow="sm" radius="md" p="md">
        <Group align="flex-end">
          <TextInput
            label="Arama"
            placeholder="Endpoint veya fonksiyon ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftSection={<IconSearch size={16} />}
            style={{ flex: 1 }}
          />
          
          {/* Yeni Router Seçici */}
          <Select
            label="Modül (Router) Seçin"
            data={uniqueRouters}
            value={selectedRouter}
            onChange={setSelectedRouter}
            leftSection={<IconFilter size={16} />}
            style={{ width: 250 }}
            searchable // Çok fazla router olursa yazarak da aranabilsin
          />
        </Group>
      </Card>

      <Card withBorder shadow="sm" radius="md" p={0}>
        <ScrollArea>
          <Table striped highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>MODÜL</Table.Th> {/* Yeni Sütun */}
                <Table.Th>METHOD</Table.Th>
                <Table.Th>ENDPOINT</Table.Th>
                <Table.Th>FONKSİYON</Table.Th>
                <Table.Th style={{ width: 120, textAlign: 'center' }}>İŞLEMLER</Table.Th>
              </Table.Tr>
            </Table.Thead>
            
            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={5} style={{ textAlign: 'center', padding: '40px' }}>
                    <Text c="dimmed">Endpointler yükleniyor...</Text>
                  </Table.Td>
                </Table.Tr>
              ) : filteredEndpoints.length > 0 ? (
                filteredEndpoints.map((endpoint, index) => (
                  <Table.Tr key={index}>
                    
                    {/* Yeni Eklenen Modül (Router) Sütunu */}
                    <Table.Td>
                      <Badge color="gray" variant="dot">
                        {endpoint.router || "Genel"}
                      </Badge>
                    </Table.Td>

                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      <Group gap="xs">
                        {(endpoint.methods || []).map((method) => (
                          <Badge key={method} color={getMethodColor(method)} variant="light">
                            {method}
                          </Badge>
                        ))}
                      </Group>
                    </Table.Td>

                    <Table.Td>
                      <Code color="blue.1" fw={600} style={{ fontSize: '13px' }}>
                        {endpoint.path}
                      </Code>
                    </Table.Td>

                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {endpoint.fonksiyon || "-"}
                      </Text>
                    </Table.Td>

                    <Table.Td>
                      <Group gap="xs" justify="center" wrap="nowrap">
                        <Tooltip label="Kopyala" withArrow position="top">
                          <ActionIcon variant="default" size="md" onClick={() => handleCopy(endpoint.path)}>
                            <IconCopy size={16} />
                          </ActionIcon>
                        </Tooltip>

                        <Tooltip label="Swagger'da Test Et" withArrow position="top">
                          <ActionIcon variant="light" color="teal" size="md" onClick={() => window.open("http://localhost:8000/docs", "_blank")}>
                            <IconExternalLink size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                    
                  </Table.Tr>
                ))
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={5} style={{ textAlign: 'center', padding: '40px' }}>
                    <Text c="dimmed">Arama kriterlerine uygun endpoint bulunamadı.</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Card>
    </Stack>
  );
}